/**
 * Navegación enterprise — intersección modulosActivos ∩ rol ∩ especialidad (doc §12).
 * Una sola fuente de verdad para menú lateral y tabs legacy.
 */

export type OrgTipo =
  | 'hospital-privado'
  | 'clinica-medica'
  | 'clinica-odontologica'
  | 'clinica-veterinaria'
  | 'clinica-nutricion'
  | 'clinica-psicologia'
  | 'clinica-estetica'
  | 'clinica-optica'
  | 'clinica-fisioterapia';

export type Tab =
  | 'dashboard'
  | 'pacientes' | 'camas' | 'ingresos' | 'notas' | 'expediente' | 'cuentas' | 'quirofano'
  | 'farmacia' | 'farmaciaPos' | 'laboratorio' | 'imagenologia' | 'signos' | 'episodios'
  | 'semaforo' | 'kardex' | 'altas'
  | 'bodega' | 'tesoreria' | 'nomina' | 'contabilidad' | 'aseguradoras' | 'consultorios' | 'profesionales'
  | 'compartido' | 'reportes' | 'usuarios' | 'configuracion' | 'bitacora'
  | 'agenda' | 'veterinaria' | 'expedienteVet' | 'vetInternamiento' | 'vetMostrador' | 'vetFarmacia'
  | 'optica' | 'opticaOrdenes' | 'opticaInventario';

export type NavItem = {
  id: Tab;
  label: string;
  icon: string;
  /** Slug @RequiresModule; omitir = siempre visible (p. ej. dashboard). */
  module?: string;
};

export type NavSection = {
  id: string;
  label: string;
  items: NavItem[];
};

/** Mapeo pestaña → módulo licenciado (alineado con Plan.java y backend). */
export const TAB_MODULE: Partial<Record<Tab, string>> = {
  agenda: 'citas',
  pacientes: 'admision',
  episodios: 'admision',
  ingresos: 'hospitalizacion',
  camas: 'hospitalizacion',
  semaforo: 'hospitalizacion',
  kardex: 'farmacia-hospitalaria',
  altas: 'hospitalizacion',
  notas: 'expediente',
  expediente: 'expediente',
  signos: 'expediente',
  farmacia: 'farmacia-hospitalaria',
  farmaciaPos: 'facturacion',
  cuentas: 'facturacion',
  aseguradoras: 'aseguradoras',
  consultorios: 'facturacion',
  profesionales: 'facturacion',
  compartido: 'expediente',
  laboratorio: 'laboratorio',
  imagenologia: 'imagenologia',
  quirofano: 'quirofano',
  bodega: 'bodega',
  tesoreria: 'tesoreria',
  nomina: 'nomina',
  contabilidad: 'contabilidad',
  reportes: 'reportes-mspas',
  usuarios: 'admision',
  configuracion: 'admision',
  bitacora: 'facturacion',
  veterinaria: 'veterinaria',
  expedienteVet: 'veterinaria',
  vetInternamiento: 'veterinaria',
  vetMostrador: 'veterinaria',
  vetFarmacia: 'veterinaria',
  optica: 'optica',
  opticaOrdenes: 'optica',
  opticaInventario: 'optica',
};

export function moduleForTab(tab: Tab): string | undefined {
  return TAB_MODULE[tab];
}

export function isTabLicensed(tab: Tab, modulosActivos: string[] | null | undefined): boolean {
  const mod = TAB_MODULE[tab];
  if (!mod) return true;
  if (!modulosActivos?.length) return true;
  return modulosActivos.includes(mod);
}

/** Catálogo sidebar — hospital privado (doc §12 wireframe). */
export function hospitalNavCatalog(): NavSection[] {
  return [
    {
      id: 'inicio',
      label: 'Inicio',
      items: [{ id: 'dashboard', label: 'Command Center', icon: '' }],
    },
    {
      id: 'atencion',
      label: 'Atención',
      items: [
        { id: 'agenda', label: 'Agenda', icon: '', module: 'citas' },
        { id: 'pacientes', label: 'Pacientes (CUI)', icon: '', module: 'admision' },
        { id: 'episodios', label: 'Episodios', icon: '', module: 'admision' },
        { id: 'ingresos', label: 'Ingresos', icon: '', module: 'hospitalizacion' },
        { id: 'expediente', label: 'Expediente clínico', icon: '', module: 'expediente' },
        { id: 'signos', label: 'Signos vitales', icon: '', module: 'expediente' },
      ],
    },
    {
      id: 'hospitalizacion',
      label: 'Hospitalización',
      items: [
        { id: 'semaforo', label: 'Semáforo de camas', icon: '', module: 'hospitalizacion' },
        { id: 'camas', label: 'Censo / camas', icon: '', module: 'hospitalizacion' },
        { id: 'kardex', label: 'Kárdex enfermería', icon: '', module: 'farmacia-hospitalaria' },
        { id: 'altas', label: 'Altas hospitalarias', icon: '', module: 'hospitalizacion' },
      ],
    },
    {
      id: 'diagnostico',
      label: 'Diagnóstico',
      items: [
        { id: 'laboratorio', label: 'Laboratorio (LIS)', icon: '', module: 'laboratorio' },
        { id: 'imagenologia', label: 'Imagenología (RIS)', icon: '', module: 'imagenologia' },
        { id: 'farmacia', label: 'Farmacia hospitalaria', icon: '', module: 'farmacia-hospitalaria' },
        { id: 'quirofano', label: 'Quirófano', icon: '', module: 'quirofano' },
      ],
    },
    {
      id: 'facturacion',
      label: 'Facturación',
      items: [
        { id: 'cuentas', label: 'Cuentas y cobros', icon: '', module: 'facturacion' },
        { id: 'farmaciaPos', label: 'Farmacia (POS)', icon: '', module: 'facturacion' },
        { id: 'aseguradoras', label: 'Aseguradoras', icon: '', module: 'aseguradoras' },
        { id: 'consultorios', label: 'Consultorios', icon: '', module: 'facturacion' },
        { id: 'profesionales', label: 'Profesionales', icon: '', module: 'facturacion' },
        { id: 'compartido', label: 'Resultados clínica', icon: '', module: 'expediente' },
      ],
    },
    {
      id: 'administracion',
      label: 'Administración',
      items: [
        { id: 'bodega', label: 'Bodega / compras', icon: '', module: 'bodega' },
        { id: 'tesoreria', label: 'Tesorería', icon: '', module: 'tesoreria' },
        { id: 'nomina', label: 'Nómina', icon: '', module: 'nomina' },
        { id: 'contabilidad', label: 'Contabilidad', icon: '', module: 'contabilidad' },
        { id: 'usuarios', label: 'Usuarios y roles', icon: '', module: 'admision' },
        { id: 'configuracion', label: 'Marca y firmas', icon: '', module: 'admision' },
        { id: 'bitacora', label: 'Bitácora', icon: '📜', module: 'facturacion' },
      ],
    },
    {
      id: 'reportes',
      label: 'Regulatorio',
      items: [{ id: 'reportes', label: 'Indicadores MSPAS', icon: '', module: 'reportes-mspas' }],
    },
  ];
}

/**
 * Pestañas permitidas por rubro — clínica privada (plan Plus = todos los módulos del rubro).
 */
export const CLINIC_SPECIALTY_TABS: Partial<Record<OrgTipo, Tab[]>> = {
  'clinica-medica': [
    'dashboard', 'agenda', 'pacientes', 'expediente', 'signos',
    'laboratorio', 'imagenologia', 'farmaciaPos', 'cuentas', 'aseguradoras', 'consultorios', 'profesionales',
    'bodega', 'tesoreria', 'usuarios', 'configuracion', 'bitacora',
  ],
  'clinica-odontologica': [
    'dashboard', 'agenda', 'pacientes', 'expediente',
    'farmaciaPos', 'cuentas', 'aseguradoras', 'consultorios', 'profesionales',
    'bodega', 'tesoreria', 'usuarios', 'configuracion', 'bitacora',
  ],
  'clinica-nutricion': [
    'dashboard', 'agenda', 'pacientes', 'expediente', 'signos',
    'cuentas', 'aseguradoras', 'consultorios', 'profesionales', 'bodega', 'tesoreria', 'usuarios', 'configuracion', 'bitacora',
  ],
  'clinica-psicologia': [
    'dashboard', 'agenda', 'pacientes', 'expediente',
    'cuentas', 'aseguradoras', 'consultorios', 'profesionales', 'bodega', 'tesoreria', 'usuarios', 'configuracion', 'bitacora',
  ],
  'clinica-estetica': [
    'dashboard', 'agenda', 'pacientes', 'expediente', 'signos',
    'farmaciaPos', 'cuentas', 'aseguradoras', 'consultorios', 'profesionales',
    'bodega', 'tesoreria', 'usuarios', 'configuracion', 'bitacora',
  ],
  'clinica-fisioterapia': [
    'dashboard', 'agenda', 'pacientes', 'expediente', 'signos',
    'cuentas', 'aseguradoras', 'consultorios', 'profesionales', 'bodega', 'tesoreria', 'usuarios', 'configuracion', 'bitacora',
  ],
};

/** Tabs extra solo en plan Clínica Plus (legacy — ya incluidos arriba). */
const CLINIC_PLUS_TABS: Tab[] = ['bodega', 'tesoreria', 'usuarios', 'configuracion', 'bitacora'];

export function specialtyTabsFor(org: OrgTipo | string | null): Set<Tab> | null {
  if (!org || org === 'hospital-privado') return null;
  if (org === 'clinica-veterinaria' || org === 'clinica-optica') return null;
  const tabs = CLINIC_SPECIALTY_TABS[org as OrgTipo];
  return tabs ? new Set(tabs) : null;
}

function clinicPlusAdminItems(): NavItem[] {
  return [
    { id: 'bodega', label: 'Inventario', icon: '', module: 'bodega' },
    { id: 'tesoreria', label: 'Tesorería', icon: '', module: 'tesoreria' },
    { id: 'usuarios', label: 'Usuarios', icon: '', module: 'admision' },
    { id: 'configuracion', label: 'Marca y firmas', icon: '', module: 'admision' },
    { id: 'bitacora', label: 'Bitácora', icon: '📜', module: 'facturacion' },
  ];
}

export function isClinicOrgTipo(org: OrgTipo | string | null | undefined): boolean {
  return !!org && org.startsWith('clinica-');
}

/** Catálogo sidebar — clínica privada ambulatoria por rubro (sin vínculo con hospital). */
export function clinicNavCatalog(orgTipo: OrgTipo | string | null, edicion?: string | null): NavSection[] {
  const org = (orgTipo ?? 'clinica-medica') as OrgTipo;
  const isPlus = edicion === 'clinic-network';

  const inicio: NavSection = {
    id: 'inicio',
    label: 'Inicio',
    items: [{ id: 'dashboard', label: 'Mi clínica', icon: '' }],
  };

  const atencionBase: NavItem[] = [
    { id: 'agenda', label: 'Agenda', icon: '', module: 'citas' },
    { id: 'pacientes', label: 'Pacientes (CUI)', icon: '', module: 'admision' },
    { id: 'expediente', label: 'Expediente clínico', icon: '', module: 'expediente' },
  ];

  const signos: NavItem = { id: 'signos', label: 'Signos vitales', icon: '', module: 'expediente' };
  const lab: NavItem = { id: 'laboratorio', label: 'Laboratorio (LIS)', icon: '', module: 'laboratorio' };
  const imagen: NavItem = { id: 'imagenologia', label: 'Imagenología (RIS)', icon: '', module: 'imagenologia' };
  const farmaciaPos: NavItem = { id: 'farmaciaPos', label: 'Mostrador (POS)', icon: '', module: 'facturacion' };
  const cuentas: NavItem = { id: 'cuentas', label: 'Cobros / FEL', icon: '', module: 'facturacion' };
  const aseguradoras: NavItem = { id: 'aseguradoras', label: 'Aseguradoras', icon: '', module: 'aseguradoras' };
  const consultorios: NavItem = { id: 'consultorios', label: 'Consultorios', icon: '', module: 'facturacion' };
  const profesionales: NavItem = { id: 'profesionales', label: 'Profesionales', icon: '', module: 'facturacion' };

  switch (org) {
    case 'clinica-odontologica':
      return [
        inicio,
        {
          id: 'atencion',
          label: 'Atención',
          items: [...atencionBase],
        },
        {
          id: 'farmacia-clinica',
          label: 'Insumos',
          items: [farmaciaPos],
        },
        {
          id: 'facturacion',
          label: 'Cobros',
          items: [cuentas, aseguradoras, consultorios, profesionales],
        },
        {
          id: 'administracion',
          label: 'Administración',
          items: isPlus ? clinicPlusAdminItems() : [
            { id: 'usuarios', label: 'Usuarios', icon: '', module: 'admision' },
            { id: 'configuracion', label: 'Marca y firmas', icon: '', module: 'admision' },
            { id: 'bitacora', label: 'Bitácora', icon: '📜', module: 'facturacion' },
          ],
        },
      ];

    case 'clinica-nutricion':
      return [
        inicio,
        {
          id: 'atencion',
          label: 'Atención',
          items: [...atencionBase, signos],
        },
        {
          id: 'facturacion',
          label: 'Cobros',
          items: [cuentas, aseguradoras, consultorios, profesionales],
        },
        {
          id: 'administracion',
          label: 'Administración',
          items: isPlus ? clinicPlusAdminItems() : [
            { id: 'usuarios', label: 'Usuarios', icon: '', module: 'admision' },
            { id: 'configuracion', label: 'Marca y firmas', icon: '', module: 'admision' },
            { id: 'bitacora', label: 'Bitácora', icon: '📜', module: 'facturacion' },
          ],
        },
      ];

    case 'clinica-psicologia':
      return [
        inicio,
        {
          id: 'atencion',
          label: 'Atención',
          items: [...atencionBase],
        },
        {
          id: 'facturacion',
          label: 'Cobros',
          items: [cuentas, aseguradoras, consultorios, profesionales],
        },
        {
          id: 'administracion',
          label: 'Administración',
          items: isPlus ? clinicPlusAdminItems() : [
            { id: 'usuarios', label: 'Usuarios', icon: '', module: 'admision' },
            { id: 'configuracion', label: 'Marca y firmas', icon: '', module: 'admision' },
            { id: 'bitacora', label: 'Bitácora', icon: '📜', module: 'facturacion' },
          ],
        },
      ];

    case 'clinica-estetica':
      return [
        inicio,
        {
          id: 'atencion',
          label: 'Atención',
          items: [...atencionBase, signos],
        },
        {
          id: 'farmacia-clinica',
          label: 'Insumos',
          items: [farmaciaPos],
        },
        {
          id: 'facturacion',
          label: 'Cobros',
          items: [cuentas, aseguradoras, consultorios, profesionales],
        },
        {
          id: 'administracion',
          label: 'Administración',
          items: isPlus ? clinicPlusAdminItems() : [
            { id: 'usuarios', label: 'Usuarios', icon: '', module: 'admision' },
            { id: 'configuracion', label: 'Marca y firmas', icon: '', module: 'admision' },
            { id: 'bitacora', label: 'Bitácora', icon: '📜', module: 'facturacion' },
          ],
        },
      ];

    case 'clinica-fisioterapia':
      return [
        inicio,
        {
          id: 'atencion',
          label: 'Atención',
          items: [
            ...atencionBase,
            { id: 'signos', label: 'Evaluación funcional', icon: '', module: 'expediente' },
          ],
        },
        {
          id: 'facturacion',
          label: 'Cobros',
          items: [cuentas, aseguradoras, consultorios, profesionales],
        },
        {
          id: 'administracion',
          label: 'Administración',
          items: isPlus ? clinicPlusAdminItems() : [
            { id: 'usuarios', label: 'Usuarios', icon: '', module: 'admision' },
            { id: 'configuracion', label: 'Marca y firmas', icon: '', module: 'admision' },
            { id: 'bitacora', label: 'Bitácora', icon: '📜', module: 'facturacion' },
          ],
        },
      ];

    case 'clinica-medica':
    default:
      return [
        inicio,
        {
          id: 'atencion',
          label: 'Atención',
          items: [...atencionBase, signos],
        },
        {
          id: 'diagnostico',
          label: 'Apoyo clínico',
          items: [lab, imagen],
        },
        {
          id: 'farmacia-clinica',
          label: 'Farmacia',
          items: [farmaciaPos],
        },
        {
          id: 'facturacion',
          label: 'Cobros',
          items: [cuentas, aseguradoras, consultorios, profesionales],
        },
        {
          id: 'administracion',
          label: 'Administración',
          items: isPlus ? clinicPlusAdminItems() : [
            { id: 'usuarios', label: 'Usuarios', icon: '', module: 'admision' },
            { id: 'configuracion', label: 'Marca y firmas', icon: '', module: 'admision' },
            { id: 'bitacora', label: 'Bitácora', icon: '📜', module: 'facturacion' },
          ],
        },
      ];
  }
}

export function vetNavCatalog(edicion?: string | null): NavSection[] {
  const isPlus = edicion === 'clinic-network';
  const adminItems: NavItem[] = isPlus ? clinicPlusAdminItems() : [
    { id: 'usuarios', label: 'Usuarios', icon: '', module: 'admision' },
    { id: 'configuracion', label: 'Marca y firmas', icon: '', module: 'admision' },
    { id: 'bitacora', label: 'Bitácora', icon: '📜', module: 'facturacion' },
  ];
  const cobrosItems: NavItem[] = [
    { id: 'cuentas', label: 'Cuentas', icon: '', module: 'facturacion' },
    { id: 'aseguradoras', label: 'Aseguradoras', icon: '', module: 'aseguradoras' },
    { id: 'consultorios', label: 'Consultorios', icon: '', module: 'facturacion' },
    { id: 'profesionales', label: 'Profesionales', icon: '', module: 'facturacion' },
  ];
  return [
    { id: 'inicio', label: 'Inicio', items: [{ id: 'dashboard', label: 'Mi veterinaria', icon: '' }] },
    {
      id: 'atencion',
      label: 'Atención',
      items: [
        { id: 'agenda', label: 'Agenda', icon: '', module: 'citas' },
        { id: 'expedienteVet', label: 'Expediente — mascota', icon: '', module: 'veterinaria' },
        { id: 'veterinaria', label: 'Mascotas y dueños', icon: '', module: 'veterinaria' },
      ],
    },
    {
      id: 'hospitalizacion',
      label: 'Hospitalización',
      items: [
        { id: 'vetInternamiento', label: 'Internamiento', icon: '', module: 'veterinaria' },
      ],
    },
    {
      id: 'tienda',
      label: 'Tienda y farmacia',
      items: [
        { id: 'vetMostrador', label: 'Alimentos y accesorios', icon: '', module: 'veterinaria' },
        { id: 'vetFarmacia', label: 'Farmacia veterinaria', icon: '', module: 'veterinaria' },
        { id: 'farmaciaPos', label: 'POS general', icon: '', module: 'facturacion' },
      ],
    },
    {
      id: 'facturacion',
      label: 'Cobros',
      items: cobrosItems,
    },
    { id: 'admin', label: 'Administración', items: adminItems },
  ];
}

export function opticaNavCatalog(edicion?: string | null): NavSection[] {
  const isPlus = edicion === 'clinic-network';
  const adminItems: NavItem[] = isPlus ? clinicPlusAdminItems() : [
    { id: 'usuarios', label: 'Usuarios', icon: '', module: 'admision' },
    { id: 'configuracion', label: 'Marca y firmas', icon: '', module: 'admision' },
    { id: 'bitacora', label: 'Bitácora', icon: '📜', module: 'facturacion' },
  ];
  const cobrosItems: NavItem[] = [
    { id: 'cuentas', label: 'Cuentas', icon: '', module: 'facturacion' },
    { id: 'aseguradoras', label: 'Aseguradoras', icon: '', module: 'aseguradoras' },
    { id: 'consultorios', label: 'Consultorios', icon: '', module: 'facturacion' },
    { id: 'profesionales', label: 'Profesionales', icon: '', module: 'facturacion' },
  ];
  if (isPlus) {}
  return [
    { id: 'inicio', label: 'Inicio', items: [{ id: 'dashboard', label: 'Mi óptica', icon: '' }] },
    {
      id: 'atencion',
      label: 'Atención',
      items: [
        { id: 'agenda', label: 'Agenda', icon: '', module: 'citas' },
        { id: 'pacientes', label: 'Pacientes (CUI)', icon: '', module: 'admision' },
        { id: 'expediente', label: 'Expediente clínico', icon: '', module: 'expediente' },
        { id: 'optica', label: 'Examen optométrico', icon: '', module: 'optica' },
      ],
    },
    {
      id: 'taller',
      label: 'Taller e inventario',
      items: [
        { id: 'opticaOrdenes', label: 'Órdenes de taller', icon: '', module: 'optica' },
        { id: 'opticaInventario', label: 'Inventario (armazones/micas)', icon: '', module: 'optica' },
      ],
    },
    {
      id: 'farmacia-clinica',
      label: 'Farmacia',
      items: [
        { id: 'farmaciaPos', label: 'Mostrador (POS)', icon: '', module: 'facturacion' },
      ],
    },
    {
      id: 'facturacion',
      label: 'Cobros',
      items: cobrosItems,
    },
    { id: 'admin', label: 'Administración', items: adminItems },
  ];
}

export type NavFilterOpts = {
  modulosActivos: string[] | null | undefined;
  permittedTabs: Set<Tab> | null;
  specialtyTabs: Set<Tab> | null;
};

export function filterNavSections(sections: NavSection[], opts: NavFilterOpts): NavSection[] {
  return sections
    .map(sec => ({
      ...sec,
      items: sec.items.filter(item => {
        if (opts.specialtyTabs && !opts.specialtyTabs.has(item.id)) return false;
        if (opts.permittedTabs && !opts.permittedTabs.has(item.id)) return false;
        if (item.module && !isTabLicensed(item.id, opts.modulosActivos)) return false;
        return true;
      }),
    }))
    .filter(sec => sec.items.length > 0);
}

export function pickNavCatalog(edicion: string | null, orgTipo: string | null): NavSection[] {
  if (orgTipo === 'clinica-veterinaria') return vetNavCatalog(edicion);
  if (orgTipo === 'clinica-optica') return opticaNavCatalog(edicion);
  // Rubro clínico privado gana sobre edición hospital (demo comparte tenant hosp-demo-toscana).
  if (isClinicOrgTipo(orgTipo)) {
    return clinicNavCatalog(orgTipo, edicion);
  }
  if (edicion === 'private-hospital' || edicion === 'public-hospital' || orgTipo === 'hospital-privado') {
    return hospitalNavCatalog();
  }
  return clinicNavCatalog(orgTipo, edicion);
}

/** Tabs efectivos de clínica incluyendo extras del plan Plus. */
export function clinicTabsForOrg(org: OrgTipo | string | null, edicion?: string | null): Set<Tab> | null {
  const base = specialtyTabsFor(org);
  if (!base || edicion !== 'clinic-network') return base;
  CLINIC_PLUS_TABS.forEach(t => base.add(t));
  return base;
}

export function labelForTab(tab: Tab, sections: NavSection[]): string {
  for (const s of sections) {
    const item = s.items.find(i => i.id === tab);
    if (item) return item.label;
  }
  return tab;
}
